// @ts-strict-ignore
import { PropertyValueType } from '@project/constants/property-value-type';
import { DefinedMetaElementPropertyVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { PropertyValueVO, PropertyVO } from '@project/components/article/model/vo/property.vo';
import { PropertyUtils } from '@article/util/property-utils';
import { ArrayUtils } from '@shared/util/array-utils';

export class DefinedMetaElementPropertyUtils {
  public static getDefinedMetaElementPropertyStringValue(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
                                                         metaElementUsageIdentity: MetaElementUsageIdentityVO,
                                                         propertyName: string): string {
    const propertyValue: PropertyValueVO = this.getFirstDefinedMetaElementPropertyValue(definedMetaElementProperties,
      metaElementUsageIdentity, propertyName);
    if (!propertyValue) {
      return null;
    }
    return propertyValue.stringValue;
  }

  public static getAllDefinedMetaElementPropertyStringValues(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
                                                             propertyName: string): string[] {
    const stringValues: string[] = [];
    const properties: PropertyVO[] = this.getPropertiesByPropertyName(definedMetaElementProperties, propertyName);
    for (const property of properties) {
      const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValue(property);
      if (propertyValue && propertyValue.stringValue && stringValues.indexOf(propertyValue.stringValue) === -1) {
        stringValues.push(propertyValue.stringValue);
      }
    }
    return stringValues;
  }

  public static setDefinedMetaElementPropertyStringValue(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string, stringValue: string): void {
    const propertyValue = new PropertyValueVO();
    propertyValue.type = PropertyValueType.STRING;
    propertyValue.stringValue = stringValue;
    this.setSingleDefinedMetaElementPropertyValue(definedMetaElementProperties, metaElementUsageIdentity, propertyName, propertyValue);
  }

  public static getDefinedMetaElementPropertyFloatValue(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
                                                        metaElementUsageIdentity: MetaElementUsageIdentityVO,
                                                        propertyName: string): number {
    const propertyValue: PropertyValueVO = this.getFirstDefinedMetaElementPropertyValue(definedMetaElementProperties,
      metaElementUsageIdentity, propertyName);
    if (!propertyValue) {
      return null;
    }
    return propertyValue.floatValue;
  }

  public static getDefinedMetaElementPropertyDescription(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string): string {
    const definedMetaElementProperty: DefinedMetaElementPropertyVO = this.getDefinedMetaElementPropertyByMetaElementUsageIdentityAndPropertyName(
      definedMetaElementProperties, metaElementUsageIdentity, propertyName);
    if (!definedMetaElementProperty) {
      return null;
    }
    return definedMetaElementProperty.description;
  }

  public static setDefinedMetaElementPropertyDescription(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string, description: string): void {
    const definedMetaElementProperty: DefinedMetaElementPropertyVO = this.getDefinedMetaElementPropertyByMetaElementUsageIdentityAndPropertyName(
      definedMetaElementProperties, metaElementUsageIdentity, propertyName);
    if (!definedMetaElementProperty) {
      return;
    }
    definedMetaElementProperty.description = description;
  }

  public static setDefinedMetaElementPropertyIntValue(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string, intValue: number): void {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.INT;
    propertyValue.intValue = intValue;
    this.setSingleDefinedMetaElementPropertyValue(definedMetaElementProperties, metaElementUsageIdentity, propertyName, propertyValue);
  }

  public static setDefinedMetaElementPropertyFloatValue(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
                                                        metaElementUsageIdentity: MetaElementUsageIdentityVO,
                                                        propertyName: string, floatValue: number): void {
    const propertyValue: PropertyValueVO = new PropertyValueVO();
    propertyValue.type = PropertyValueType.FLOAT;
    propertyValue.floatValue = floatValue;
    this.setSingleDefinedMetaElementPropertyValue(definedMetaElementProperties, metaElementUsageIdentity, propertyName, propertyValue);
  }

  public static setSingleDefinedMetaElementPropertyValue(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string, propertyValue: PropertyValueVO): void {
    this.setDefinedMetaElementPropertyValues(definedMetaElementProperties, metaElementUsageIdentity, propertyName, [propertyValue]);
  }

  public static setDefinedMetaElementPropertyValues(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string, propertyValues: PropertyValueVO[]): void {
    if (!definedMetaElementProperties || !metaElementUsageIdentity || !propertyName) {
      return;
    }
    let definedMetaElementProperty = this.getDefinedMetaElementPropertyByMetaElementUsageIdentityAndPropertyName(
      definedMetaElementProperties, metaElementUsageIdentity, propertyName);
    if (!definedMetaElementProperty) {
      definedMetaElementProperty = new DefinedMetaElementPropertyVO();
      definedMetaElementProperty.identity = MetaElementUsageIdentityVO.create(metaElementUsageIdentity.articleUsageId,
        metaElementUsageIdentity.metaElementName);
      definedMetaElementProperty.property = new PropertyVO();
      definedMetaElementProperty.property.name = propertyName;
      definedMetaElementProperties.push(definedMetaElementProperty);
    }
    definedMetaElementProperty.property.values = propertyValues;
  }

  public static getDefinedMetaElementPropertyByMetaElementUsageIdentityAndPropertyName(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string): DefinedMetaElementPropertyVO {
    if (!definedMetaElementProperties || !metaElementUsageIdentity || !propertyName) {
      return null;
    }
    for (const definedMetaElementProperty of definedMetaElementProperties) {
      if (metaElementUsageIdentity.equals(definedMetaElementProperty.identity)
        && definedMetaElementProperty.property.name === propertyName) {
        return definedMetaElementProperty;
      }
    }
    return null;
  }

  public static getFirstDefinedMetaElementPropertyValue(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string): PropertyValueVO {
    return PropertyUtils.getFirstPropertyValue(this.getPropertyByMetaElementUsageIdentityAndPropertyName(definedMetaElementProperties,
      metaElementUsageIdentity, propertyName));
  }

  public static getPropertyByMetaElementUsageIdentityAndPropertyName(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: string): PropertyVO {
    const definedMetaElementProperty: DefinedMetaElementPropertyVO =
      this.getDefinedMetaElementPropertyByMetaElementUsageIdentityAndPropertyName(definedMetaElementProperties,
        metaElementUsageIdentity, propertyName);
    return !definedMetaElementProperty ? null : definedMetaElementProperty.property;
  }

  public static removePropertyByMetaElementUsageIdentityAndPropertyName(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
    metaElementUsageIdentity: MetaElementUsageIdentityVO, propertyName: String): void {
    if (ArrayUtils.isEmpty(definedMetaElementProperties) || !propertyName) {
      return;
    }
    for (const definedMetaElementProperty of definedMetaElementProperties) {
      if (metaElementUsageIdentity.equals(definedMetaElementProperty.identity)
        && definedMetaElementProperty.property.name === propertyName) {
        definedMetaElementProperties.splice(definedMetaElementProperties.indexOf(definedMetaElementProperty), 1);
        return;
      }
    }
  }

  public static getPropertiesByPropertyName(definedMetaElementProperties: DefinedMetaElementPropertyVO[],
                                            propertyName: string): PropertyVO[] {
    if (!definedMetaElementProperties || !propertyName) {
      return [];
    }
    const result: PropertyVO[] = [];
    for (const definedMetaElementProperty of definedMetaElementProperties) {
      if (definedMetaElementProperty.property.name === propertyName) {
        result.push(definedMetaElementProperty.property);
      }
    }
    return result;
  }

  public static removePropertiesByName(definedMetaElementProperties: DefinedMetaElementPropertyVO[], propertyName: string): void {
    if (!definedMetaElementProperties || definedMetaElementProperties.length === 0 || !propertyName) {
      return;
    }
    for (let i: number = 0; i < definedMetaElementProperties.length; i++) {
      const definedProperty: DefinedMetaElementPropertyVO = definedMetaElementProperties[i];
      if (definedProperty && definedProperty.property && definedProperty.property.name === propertyName) {
        definedMetaElementProperties.splice(i, 1);
        i--;
      }
    }
  }

  public static removePropertiesByNameAndStringValue(definedMetaElementProperties: DefinedMetaElementPropertyVO[], propertyName: string,
                                                     stringValue: string): void {
    if (!definedMetaElementProperties || definedMetaElementProperties.length === 0 || propertyName === null || stringValue === null) {
      return;
    }
    for (let i: number = 0; i < definedMetaElementProperties.length; i++) {
      const definedProperty: DefinedMetaElementPropertyVO = definedMetaElementProperties[i];
      if (definedProperty && definedProperty.property && definedProperty.property.name === propertyName) {
        const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValue(definedProperty.property);
        if (propertyValue && propertyValue.stringValue && propertyValue.stringValue === stringValue) {
          definedMetaElementProperties.splice(i, 1);
          i--;
        }
      }
    }
  }

  public static replaceStringPropertyValues(definedMetaElementProperties: DefinedMetaElementPropertyVO[], propertyName: string,
                                            propertyValueFrom: string, propertyValueTo: string): void {
    if (!propertyValueFrom || !propertyValueTo) {
      return;
    }
    const properties: PropertyVO[] = this.getPropertiesByPropertyName(definedMetaElementProperties, propertyName);
    for (const property of properties) {
      PropertyUtils.replaceStringValueInProperty(property, propertyValueFrom, propertyValueTo);
    }
  }
}
